import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchVideos } from "@/utils/videoHelpers";
import { categories } from "@/mocks/categories";
import SearchBar from "@/components/feature/SearchBar";
import VideoCard from "@/components/feature/VideoCard";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();

  const results = useMemo(() => (keyword ? searchVideos(keyword) : []), [keyword]);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果-91短视频` : "搜索短视频-91短视频",
    description: keyword
      ? `在91短视频搜索“${keyword}”，找到 ${results.length} 条相关热门短视频内容。`
      : "在91短视频搜索搞笑、娱乐、生活、美食等热门短视频内容。",
    keywords: "91短视频搜索,短视频搜索,热门短视频",
  });

  const hotWords = ["搞笑合集", "打工人", "猫咪", "探店", "出租屋改造", "提拉米苏"];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h1 className="text-lg font-bold text-foreground-900 md:text-xl">搜索短视频</h1>
        <p className="mt-1 text-sm text-foreground-500">
          输入关键词，快速找到你感兴趣的热门短视频
        </p>
        <div className="mt-4 max-w-2xl">
          <SearchBar />
        </div>
      </div>

      {!keyword ? (
        <div className="mt-8">
          <h2 className="text-base font-bold text-foreground-900">大家都在搜</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {hotWords.map((word) => (
              <Link
                key={word}
                to={`/search?q=${encodeURIComponent(word)}`}
                className="cursor-pointer rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-900 transition hover:bg-secondary-200"
              >
                <i className="ri-search-line mr-1"></i>
                {word}
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <h2 className="text-base font-bold text-foreground-900">按分类浏览</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                >
                  <i className={`${category.icon} text-xl text-accent-500`}></i>
                  {category.name}短视频
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <p className="text-sm text-foreground-600">
            关于
            <span className="mx-1 font-semibold text-primary-600">“{keyword}”</span>
            的搜索结果，共 <span className="font-semibold text-foreground-900">{results.length}</span> 条
          </p>

          {results.length === 0 ? (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-20 text-center">
              <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
              <h3 className="mt-4 text-base font-semibold text-foreground-800">
                没有找到相关视频
              </h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                换个关键词试试，比如“搞笑”“美食”“生活”，或者直接返回首页看看热门推荐。
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/"
                  className="cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
                >
                  返回首页
                </Link>
                <Link
                  to="/category/comedy"
                  className="cursor-pointer rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                >
                  浏览搞笑分类
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {results.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}